import { hasUnsaved } from "@/lib/unsavedChanges";   // Batch 27 · Add-on 3 · C (M41)
// Batch 10 · B16 — a tab opened before a release asks for a screen chunk that the new build no longer has
// ("Loading chunk 151 failed (missing …/static/js/151.ae9501c9.chunk.js)"). Reload the page ONCE so it picks up the new build;
// a second chunk error within a minute shows the normal error screen (guard against reload loops). Tenant and Founder portals.
const KEY = "af_chunk_reload_at";
const WINDOW_MS = 60 * 1000;

export function isChunkLoadError(err) {
  if (!err) return false;
  const name = String(err.name || "");
  const msg = String(err.message || err.reason?.message || err || "");
  return name === "ChunkLoadError" || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg) || /Failed to fetch dynamically imported module/i.test(msg);
}

function lastReloadAt() {
  try { return Number(sessionStorage.getItem(KEY) || 0); } catch { return 0; }
}

/** True when the page may reload for a chunk error now (no such reload in the last minute). Pure: no side effect. */
export function mayReloadForChunk(err, now = Date.now()) {
  // Batch 27 · Add-on 3 · C (M41): never by itself while a form has unsaved changes (the new-version banner offers it)
  return isChunkLoadError(err) && now - lastReloadAt() > WINDOW_MS && !hasUnsaved();
}

/** Reload once for a chunk error. Returns true when a reload was started (the caller shows nothing / a short "Updating…"). */
export function reloadOnceForChunkError(err, now = Date.now()) {
  if (!mayReloadForChunk(err, now)) return false;
  try { sessionStorage.setItem(KEY, String(now)); } catch { return false; }   // no storage → no guard → never auto-reload
  stashForRecoveredReport(err, now);                                         // STEP 10 · C2: reported after the reload
  try { window.location.reload(); } catch { return false; }
  return true;
}

// STEP 10 · C2 (Errors tab #2 / #3 / #11): the error record is kept, but a chunk error that the ONE reload cured is not a pending
// error. Before the reload the error is kept in sessionStorage; the reloaded page reports it RECOVERED_REPORT_DELAY_MS later with
// ``recovered: true`` (the server files it as a notice — group "Info", not pending). If the chunk fails again in that page (the
// reload did not cure it) the error screen reports it as an ordinary error (Pending) and the recovered report is dropped.
const ERR_KEY = "af_chunk_reload_err";
export const RECOVERED_REPORT_DELAY_MS = 20 * 1000;
let failedAgain = false;

function stashForRecoveredReport(err, now) {
  try {
    sessionStorage.setItem(ERR_KEY, JSON.stringify({
      name: String((err && err.name) || "ChunkLoadError").slice(0, 80),
      message: String((err && (err.message || err.reason?.message)) || err || "").slice(0, 500),
      pathname: (typeof window !== "undefined" && window.location?.pathname) || "",
      at: new Date(now).toISOString(),
    }));
  } catch { /* no storage: nothing is reported, as before */ }
}

/** A chunk error the page could not reload for (the reload did not cure it): the recovered report must not be sent. */
export function markChunkFailedAgain() {
  failedAgain = true;
  try { sessionStorage.removeItem(ERR_KEY); } catch { /* ignore */ }
}

/** Send the kept chunk error as recovered (once). ``post`` = (url, body, opts) → Promise. True when it was sent. */
export function reportRecoveredChunk(post) {
  let raw = null;
  try { raw = sessionStorage.getItem(ERR_KEY); } catch { return false; }
  if (!raw || failedAgain) return false;
  try { sessionStorage.removeItem(ERR_KEY); } catch { /* ignore */ }
  let rec = null;
  try { rec = JSON.parse(raw); } catch { return false; }
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  Promise.resolve(post("/client-errors", { ...rec, user_agent: ua, recovered: true }, { _silent: true })).catch(() => {});
  return true;
}

export function scheduleRecoveredChunkReport(post, delay = RECOVERED_REPORT_DELAY_MS) {
  return setTimeout(() => { try { reportRecoveredChunk(post); } catch { /* never breaks the page */ } }, delay);
}
